import { ref, computed, watch, type Ref, type ComputedRef } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import {
  searchVault,
  type ContractManagementContractInDbSlim,
  type VaultDirectoryInDbSlim,
  type VaultFileInDb,
  type VaultSearchResult,
} from '@/api';

const PAGE_SIZE = 20;

export type SearchTab = 'contracts' | 'directories' | 'files';

interface TabState<T> {
  items: Ref<T[]>;
  total: Ref<number>;
  loading: Ref<boolean>;
  hasMore: ComputedRef<boolean>;
}

function createTabState<T>(): TabState<T> {
  const items = ref<T[]>([]) as Ref<T[]>;
  const total = ref(0);
  return {
    items,
    total,
    loading: ref(false),
    hasMore: computed(() => items.value.length < total.value),
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useGlobalSearch() {
  const isOpen = ref(false);
  const searchText = ref('');
  const activeTab = ref<SearchTab>('contracts');

  // One vault search returns all three lists, each paginated on its own by
  // the shared limit/offset.
  const contracts = createTabState<ContractManagementContractInDbSlim>();
  const directories = createTabState<VaultDirectoryInDbSlim>();
  const files = createTabState<VaultFileInDb>();

  const tabs = { contracts, directories, files };

  function merge<T>(state: TabState<T>, items: T[], total: number, append: boolean): void {
    state.items.value = append ? [...state.items.value, ...items] : items;
    state.total.value = total;
  }

  /** Write a response into every tab, or append it to `only` when loading more. */
  function applyResults(data: VaultSearchResult, only?: SearchTab): void {
    const append = only !== undefined;
    if (!only || only === 'contracts')
      merge(contracts, data.contract_results, data.contract_results_count, append);
    if (!only || only === 'directories')
      merge(directories, data.directory_results, data.directory_results_count, append);
    if (!only || only === 'files') merge(files, data.file_results, data.file_results_count, append);
  }

  // Bumped on every new search, so a slow response for stale text is dropped.
  let searchId = 0;

  async function searchAll(): Promise<void> {
    if (searchText.value.length < 2) return;
    const id = ++searchId;
    resetAll();
    for (const t of Object.values(tabs)) t.loading.value = true;
    try {
      const response = await searchVault({
        body: { text: searchText.value, limit: PAGE_SIZE, offset: 0 },
      });
      if (id !== searchId || !response.data) return;
      applyResults(response.data);
    } catch {
      // silently fail — user sees empty results
    } finally {
      if (id === searchId) for (const t of Object.values(tabs)) t.loading.value = false;
    }
  }

  async function loadMore(): Promise<void> {
    const tab = activeTab.value;
    const state = tabs[tab];
    if (state.loading.value || !state.hasMore.value) return;
    const id = searchId;
    state.loading.value = true;
    try {
      const response = await searchVault({
        body: { text: searchText.value, limit: PAGE_SIZE, offset: state.items.value.length },
      });
      if (id !== searchId || !response.data) return;
      applyResults(response.data, tab);
    } catch {
      // silently fail
    } finally {
      if (id === searchId) state.loading.value = false;
    }
  }

  const debouncedSearch = useDebounceFn(searchAll, 300);

  function resetAll(): void {
    for (const t of Object.values(tabs)) {
      t.items.value = [];
      t.total.value = 0;
      t.loading.value = false;
    }
  }

  function open(): void {
    isOpen.value = true;
  }

  function close(): void {
    isOpen.value = false;
  }

  watch(searchText, val => {
    if (val.length >= 2) {
      void debouncedSearch();
    } else {
      searchId++;
      resetAll();
    }
  });

  // Reset on close
  watch(isOpen, val => {
    if (!val) {
      searchText.value = '';
      activeTab.value = 'contracts';
      searchId++;
      resetAll();
    }
  });

  return {
    isOpen,
    searchText,
    activeTab,
    open,
    close,
    loadMore,
    contracts,
    directories,
    files,
  };
}

export type GlobalSearchReturn = ReturnType<typeof useGlobalSearch>;
